import { Box, Button, Chip, IconButton, Snackbar, useTheme } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { tokens } from '../../theme';
import Header from '../../components/Header';
import ConfirmDeleteDialog from '../../components/ConfirmDeleteDialog';

const Roles = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const token = localStorage.getItem('token');
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastmessage] = useState('');
  const [idToDelete, setIdToDelete] = useState(null);

  const getData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/roles`, { headers: { token } });
      const data = await res.json();
      if (data.success) setRoles(data.data);
    } catch (e) {
      console.error('Roles fetch error', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmDelete = async () => {
    if (idToDelete == null) return;
    const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/roles/${idToDelete}`, {
      method: 'DELETE',
      headers: { token },
    });
    const data = await res.json();
    setIdToDelete(null);
    if (data.message) {
      setOpenToast(true);
      setToastmessage(data.message);
    }
    if (data.success) getData();
  };

  const columns = [
    { field: 'name', headerName: 'Role', flex: 1, minWidth: 160, cellClassName: 'name-column--cell' },
    { field: 'description', headerName: 'Description', flex: 1.5, minWidth: 200 },
    {
      field: 'dataScope',
      headerName: 'Destinations',
      flex: 0.7,
      minWidth: 130,
      valueGetter: (params) => (params.row.dataScope === 'assigned' ? 'Assigned only' : 'All'),
    },
    {
      field: 'permissionCount',
      headerName: 'Permissions',
      flex: 0.6,
      minWidth: 110,
      renderCell: ({ row }) =>
        row.isSystem ? (
          <Chip size="small" label="Fixed (system role)" sx={{ backgroundColor: colors.grey[700] }} />
        ) : (
          row.permissionCount
        ),
    },
    { field: 'userCount', headerName: 'Users', flex: 0.4, minWidth: 80 },
    {
      field: 'Edit',
      sortable: false,
      renderCell: ({ row }) =>
        row.isSystem ? null : (
          <Link to={`/roles/${row.id}`}>
            <Button
              sx={{
                backgroundColor: colors.greenAccent[600],
                color: colors.grey[100],
                ':hover': { backgroundColor: colors.greenAccent[700] },
              }}
              variant="contained"
            >
              <EditIcon />
            </Button>
          </Link>
        ),
    },
    {
      field: 'Delete',
      sortable: false,
      renderCell: ({ row }) =>
        row.isSystem ? null : (
          <Button
            sx={{
              backgroundColor: colors.redAccent[500],
              color: colors.grey[100],
              ':hover': { backgroundColor: colors.redAccent[600] },
            }}
            variant="contained"
            onClick={() => setIdToDelete(row.id)}
          >
            <DeleteIcon />
          </Button>
        ),
    },
  ];

  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpenToast(false);
  };

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" gap="10px" flexWrap="wrap">
        <Header title="ROLES & PERMISSIONS" subtitle="Create roles and choose what each role can access" />

        <Box display="flex" gap="10px" flexWrap="wrap">
          <Link to="/adminusers">
            <Button
              sx={{
                backgroundColor: colors.blueAccent[700],
                color: colors.grey[100],
                fontSize: '14px',
                fontWeight: 'bold',
                padding: '10px 20px',
                '&:hover': { backgroundColor: colors.blueAccent[800] },
              }}
            >
              Admin Users
            </Button>
          </Link>
          <Link to="/roles/add">
            <Button
              sx={{
                backgroundColor: colors.blueAccent[700],
                color: colors.grey[100],
                fontSize: '14px',
                fontWeight: 'bold',
                padding: '10px 20px',
                '&:hover': { backgroundColor: colors.blueAccent[800] },
              }}
            >
              <AddIcon sx={{ mr: '10px' }} />
              Add Role
            </Button>
          </Link>
        </Box>
      </Box>

      <Box
        m="10px 0 0 0"
        height="560px"
        sx={{
          '& .MuiDataGrid-root': { border: 'none' },
          '& .MuiDataGrid-cell': { borderBottom: 'none' },
          '& .MuiDataGrid-columnHeaders': { backgroundColor: colors.blueAccent[700], borderBottom: 'none' },
          '& .MuiDataGrid-virtualScroller': { backgroundColor: colors.primary[400] },
          '& .MuiDataGrid-footerContainer': { borderTop: 'none', backgroundColor: colors.blueAccent[700] },
        }}
      >
        <DataGrid rows={roles} columns={columns} getRowId={(row) => row.id} loading={loading} disableColumnFilter />
      </Box>

      <ConfirmDeleteDialog
        open={idToDelete != null}
        onClose={() => setIdToDelete(null)}
        onConfirm={confirmDelete}
        title="Delete role?"
        message="Are you sure you want to delete this role? Roles that are assigned to users can't be deleted."
        colors={colors}
      />

      <Snackbar
        open={openToast}
        autoHideDuration={5000}
        onClose={handleCloseToast}
        message={toastMessage}
        action={
          <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseToast}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default Roles;
